import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import { useEffect, useMemo, useRef, useState } from 'react';

import { caloTheme } from '../../../assets/themes/calo';

export interface AutocompleteOption {
  label: string;
  value: string;
  station: string;
  componentName: string;
}

interface CustomAutocompleteProps {
  options: AutocompleteOption[];
  onChange: (value: AutocompleteOption | null) => void;
  placeholder?: string;
  width?: string | number;
}

const CustomAutocomplete = ({
  options,
  onChange,
  placeholder = 'Search for component',
  width = '100%'
}: CustomAutocompleteProps) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const filteredOptions = useMemo(() => {
    if (!inputValue.trim()) return options;
    const searchValue = inputValue.toLowerCase().trim();
    return options.filter((option) => {
      const componentName = option.componentName?.toLowerCase() ?? '';
      const label = option.label?.toLowerCase() ?? '';
      return componentName.includes(searchValue) || label.includes(searchValue);
    });
  }, [options, inputValue]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleSelectOption = (option: AutocompleteOption) => {
    setInputValue(option.label);
    setIsOpen(false);
    setHighlightedIndex(-1);
    onChange(option);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && filteredOptions.length > 0 && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setIsOpen(true);
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : prev));
      setIsOpen(true);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter' && highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
      e.preventDefault();
      handleSelectOption(filteredOptions[highlightedIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }
  };

  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const highlightedElement = listRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [highlightedIndex]);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: width
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: '40px',
          borderRadius: '6px',
          border: `1px solid ${caloTheme.palette.neutral300 ?? '#CCCCCC'}`,
          backgroundColor: caloTheme.palette.white,
          pl: 1.5,
          pr: 1.5,
          gap: 1,
          boxSizing: 'border-box',
          '& .custom-autocomplete-input::placeholder': {
            color: caloTheme.palette.neutral600 ?? '#757575',
            opacity: 1
          }
        }}
      >
        <SearchIcon
          sx={{
            color: caloTheme.palette.neutral600 ?? '#757575',
            fontSize: 22,
            flexShrink: 0
          }}
        />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={(e) => {
            if (e.key === 'a') {
              e.stopPropagation();
            }
            handleKeyDown(e);
          }}
          placeholder={placeholder}
          inputMode="search"
          autoComplete="off"
          style={{
            flex: 1,
            minWidth: 0,
            height: '100%',
            padding: 0,
            border: 'none',
            outline: 'none',
            fontSize: 14,
            fontWeight: 400,
            color: caloTheme.palette.neutral900,
            backgroundColor: 'transparent',
            boxSizing: 'border-box',
            WebkitAppearance: 'none',
            WebkitTapHighlightColor: 'transparent'
          }}
          className="custom-autocomplete-input"
        />
        <CloseIcon
          onClick={() => {
            setInputValue('');
            setIsOpen(false);
            setHighlightedIndex(-1);
            onChange(null);
            inputRef.current?.focus();
          }}
          sx={{
            color: caloTheme.palette.neutral600 ?? '#757575',
            fontSize: 20,
            cursor: 'pointer',
            visibility: inputValue ? 'visible' : 'hidden',
            justifySelf: 'flex-end'
          }}
        />
      </Box>
      {isOpen && filteredOptions.length > 0 && (
        <Box
          component="ul"
          ref={listRef}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 10000,
            margin: 0,
            padding: 0,
            listStyle: 'none',
            backgroundColor: caloTheme.palette.white,
            border: `1px solid ${caloTheme.palette.neutral300 || '#ccc'}`,
            borderRadius: '6px',
            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
            maxHeight: '140px',
            overflowY: 'auto',
            mt: 0.5,
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {filteredOptions.map((option, index) => (
            <Box
              component="li"
              key={`${option.value}-${option.station}`}
              onClick={() => handleSelectOption(option)}
              onMouseEnter={() => setHighlightedIndex(index)}
              onTouchStart={() => setHighlightedIndex(index)}
              sx={{
                padding: '10px 12px',
                cursor: 'pointer',
                backgroundColor: highlightedIndex === index ? caloTheme.palette.primary100 || '#f0f0f0' : 'transparent',
                '&:hover': {
                  backgroundColor: caloTheme.palette.primary100 || '#f0f0f0'
                },
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent'
              }}
            >
              {option.label}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default CustomAutocomplete;
